import React from "react";
import { NavLink, useNavigate } from "react-router-dom";
import { LayoutDashboard, LineChart, Boxes, Truck, Users, LogOut, BarChart3, ChevronsLeft, ChevronsRight, Sparkles } from "lucide-react";
import { AngadyaLogo } from "./Logo";
import { useSession } from "../context/SessionContext";

const NAV = [
  { to: "/app/overview", label: "Overview", icon: LayoutDashboard, module: "overview" },
  { to: "/app/sales", label: "Sales", icon: LineChart, module: "sales" },
  { to: "/app/inventory", label: "Inventory", icon: Boxes, module: "inventory" },
  { to: "/app/procurement", label: "Procurement", icon: Truck, module: "procurement" },
  { to: "/app/hr", label: "HR & Staffing", icon: Users, module: "hr" },
  { to: "/app/analytics", label: "Analytics", icon: BarChart3, module: "analytics" },
  { to: "/app/ask", label: "Ask", icon: Sparkles, module: "ask" },
];

export const Sidebar = ({ open = false, onNavItemClick, collapsed = false, onToggleCollapse }) => {
  const { session, bootstrap, roleMeta, allowedModules, logout } = useSession();
  const navigate = useNavigate();

  const scopeLine = (() => {
    if (!session) return "";
    if (session.role === "store_manager" && session.branch_id) {
      const b = bootstrap?.branches.find((x) => x.id === session.branch_id);
      return b ? `${b.name} · ${b.region}` : "Single branch";
    }
    if (session.role === "regional_manager" && session.region) {
      const count = bootstrap?.branches.filter((b) => b.region === session.region).length || 0;
      return `${session.region} Chennai · ${count} branches`;
    }
    return "All 27 branches";
  })();

  const items = NAV.filter((n) => allowedModules.includes(n.module));

  return (
    <aside
      className={`shrink-0 flex flex-col text-white h-screen fixed top-0 left-0 z-40 lg:sticky lg:top-0 lg:z-30 transform transition-[transform,width] duration-200 ease-out ${
        open ? "translate-x-0" : "-translate-x-full lg:translate-x-0"
      } ${collapsed ? "w-16" : "w-64"}`}
      style={{ background: "#14142B" }}
      data-testid="sidebar"
      data-collapsed={collapsed ? "true" : "false"}
    >
      {/* Header — logo lockup */}
      <div
        className={`pt-6 pb-5 flex items-center ${collapsed ? "px-2 justify-center" : "px-5 gap-3"}`}
      >
        <AngadyaLogo size={collapsed ? 44 : 56} />
        {!collapsed && (
          <div className="min-w-0">
            <div
              className="text-xl font-extrabold leading-tight"
              style={{ letterSpacing: "-0.028em", color: "#FFFFFF" }}
            >
              Angadya
            </div>
            <div
              className="text-[9px] font-bold uppercase tracking-widest mt-0.5"
              style={{ color: "#8E90A8" }}
            >
              Retail Intelligence
            </div>
          </div>
        )}
      </div>

      {/* Collapse toggle — desktop only */}
      <button
        type="button"
        onClick={onToggleCollapse}
        data-testid="btn-toggle-sidebar-collapse"
        aria-label={collapsed ? "Expand sidebar" : "Collapse sidebar"}
        title={collapsed ? "Expand sidebar" : "Collapse sidebar"}
        className="hidden lg:flex absolute -right-3 top-8 z-30 w-6 h-6 items-center justify-center rounded-full transition-transform hover:scale-110"
        style={{
          background: "linear-gradient(135deg, #00B4D8 0%, #7C6FF0 100%)",
          color: "#FFFFFF",
          boxShadow: "0 4px 10px rgba(0,180,216,0.35)",
        }}
      >
        {collapsed ? <ChevronsRight size={13} strokeWidth={2.4} /> : <ChevronsLeft size={13} strokeWidth={2.4} />}
      </button>

      {/* Nav */}
      <nav className={`flex-1 overflow-y-auto ${collapsed ? "py-3 px-2" : "py-4 px-3"}`}>
        {items.map((n) => (
          <NavLink
            key={n.to}
            to={n.to}
            data-testid={`nav-${n.module}`}
            onClick={() => onNavItemClick && onNavItemClick()}
            title={collapsed ? n.label : undefined}
            className={({ isActive }) =>
              `group relative flex items-center rounded-xl transition-all mb-1 ${
                collapsed ? "justify-center p-2.5" : "gap-3 px-3.5 py-2.5"
              } text-sm font-semibold ${
                isActive
                  ? "text-white"
                  : "text-white/70 hover:text-white hover:bg-white/[0.06]"
              }`
            }
            style={({ isActive }) =>
              isActive
                ? {
                    background: "linear-gradient(135deg, rgba(0,180,216,0.20) 0%, rgba(124,111,240,0.18) 100%)",
                    boxShadow: "inset 0 0 0 1px rgba(255,255,255,0.06)",
                  }
                : {}
            }
          >
            {({ isActive }) => (
              <>
                {isActive && (
                  <span
                    className="absolute left-0 top-2 bottom-2 w-[3px] rounded-r-full"
                    style={{ background: "linear-gradient(180deg, #00B4D8, #7C6FF0)" }}
                  />
                )}
                <n.icon size={collapsed ? 19 : 17} strokeWidth={2} />
                {!collapsed && <span>{n.label}</span>}
              </>
            )}
          </NavLink>
        ))}
      </nav>

      {/* Role card — soft, rounded */}
      <div className={collapsed ? "mt-auto px-2 py-4 flex flex-col items-center gap-3" : "mt-auto p-4"}>
        {!collapsed ? (
          <div
            className="rounded-2xl p-4"
            style={{
              background: "rgba(255,255,255,0.05)",
              boxShadow: "inset 0 0 0 1px rgba(255,255,255,0.06)",
            }}
          >
            <div className="text-[9px] font-bold uppercase tracking-widest" style={{ color: "#8E90A8" }}>
              Signed in as
            </div>
            <div className="text-base font-extrabold mt-1" style={{ letterSpacing: "-0.01em" }}>
              {roleMeta?.label || "—"}
            </div>
            <div className="text-[11px] mt-0.5 leading-snug" style={{ color: "rgba(255,255,255,0.65)" }}>
              {scopeLine}
            </div>
            <button
              data-testid="btn-switch-role"
              onClick={() => {
                logout();
                navigate("/");
              }}
              className="mt-3 w-full flex items-center justify-between rounded-xl px-3 py-2 text-[11px] font-bold uppercase tracking-widest text-white/85 hover:text-white transition-colors"
              style={{ background: "rgba(255,255,255,0.05)" }}
            >
              <span>Switch Role</span>
              <LogOut size={13} strokeWidth={2.2} />
            </button>
          </div>
        ) : (
          <button
            data-testid="btn-switch-role"
            onClick={() => {
              logout();
              navigate("/");
            }}
            title={`${roleMeta?.label || "Signed in"} · Switch role`}
            className="w-10 h-10 flex items-center justify-center rounded-xl text-white/85 hover:text-white transition-colors"
            style={{ background: "rgba(255,255,255,0.06)" }}
          >
            <LogOut size={15} strokeWidth={2.2} />
          </button>
        )}
      </div>
    </aside>
  );
};

export default Sidebar;
